import React from "react";
import { Calendar, Users, MapPin, Compass, Network, BarChart3, Ticket } from "lucide-react";
import { FeatureHero, FeatureRow, IconGrid, PhotoBand, StatBand, ClosingCta, FeatureFooterNav, Shot } from "@/components/features/FeatureKit";
import { ProfileFieldsMock, KpiMock } from "@/components/features/mocks";

const PHOTO = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800";

export default function EventsFeature() {
  return (
    <div className="overflow-x-hidden" data-testid="feature-events-page">
      <FeatureHero
        eyebrow="Events"
        icon={Calendar}
        title="Find out where everyone's swimming."
        copy="Discover startup gatherings, meetups, workshops, dinners, conferences, pitch competitions, and more."
        ctaLabel="Explore Events"
        ctaTo="/events"
        visual={<Shot src="/features/event.png" alt="A Startup Otter event page for Pre-Seed Pitch Night, with cover image and organizer" url="startupotter.com/event/pre-seed-pitch-night" testId="events-hero-visual" priority />}
      />

      <FeatureRow
        testId="events-create"
        label="Create"
        icon={Ticket}
        title="Create and share events."
        copy="Give your event a dedicated presence with the information people need to understand what's happening."
        visual={<ProfileFieldsMock title="Event details" testId="mock-event-details" items={[["When", "Monday, August 10 · 6:00 PM (America/Chicago)"], ["Where", "Chicago, IL, US · In person"], ["Cost", "Free"], ["Topics", "Fundraising · SaaS"]]} />}
      />

      <PhotoBand
        testId="events-gathering"
        src={PHOTO}
        alt="A packed audience at a startup event"
        kicker="Discovery"
        title="Find what's happening around you."
        copy="Location-aware discovery helps members uncover events relevant to their community — and Local Mode narrows all of Startup Otter to what's closer to home."
      />

      <FeatureRow
        testId="events-organizer"
        tone="dark"
        flip
        label="Organizers"
        icon={Users}
        title="Meet the people behind it."
        copy="Events connect back to their organizers, making it easy to explore the people and organizations creating the experience."
        visual={<Shot src="/features/event.png" alt="An event page linking through to the organization hosting it" url="startupotter.com/event/pre-seed-pitch-night" dark cropClass="h-[420px]" testId="events-organizer-shot" />}
      />

      <FeatureRow
        testId="events-location"
        tone="tint"
        label="Nearby"
        icon={MapPin}
        title="Browse the calendar, filtered to you."
        copy="Sort by date, format and distance so the events you can actually attend surface first."
        visual={<Shot src="/features/events-directory.png" alt="Browsing Startup Otter events with filters for date, format and distance" url="startupotter.com/events" testId="events-directory-shot" />}
      />

      <IconGrid
        testId="events-ecosystem"
        tone="brand"
        columns={3}
        title="One event, several doors."
        items={[
          { icon: Compass, title: "Go local.", copy: "Use Local Mode when you want to focus Startup Otter on what's happening closer to home." },
          { icon: Network, title: "Keep exploring.", copy: "An Event can lead to an organizer, a Business, a Personal Profile, a conversation, or another opportunity across Startup Otter." },
          { icon: Users, title: "Meet the room.", copy: "The people you meet at an event are a click away long after it ends." },
        ]}
      />

      <FeatureRow
        testId="events-analytics"
        label="Analytics"
        icon={BarChart3}
        title="See what gets attention."
        copy="Event Analytics help organizers understand views and important engagement actions around their events."
        visual={<KpiMock testId="mock-event-kpis" items={[{ label: "Event visits", value: "684", delta: "+14%" }, { label: "Unique visitors", value: "402", delta: "+11%" }, { label: "Interested", value: "58", delta: "+9" }, { label: "CTA clicks", value: "77", delta: "+6" }]} />}
      />

      <StatBand
        testId="events-stats"
        tone="dark"
        items={[
          { value: "In person", label: "Meetups, dinners, workshops and pitch nights." },
          { value: "Virtual", label: "Anything the community can join from anywhere." },
          { value: "Hybrid", label: "Both, without maintaining two listings." },
        ]}
      />

      <ClosingCta
        testId="events-closing"
        title="The event is one moment. The community lasts longer."
        ctaLabel="Explore Events"
        ctaTo="/events"
      />

      <FeatureFooterNav next={{ to: "/features/resources", label: "Resources", desc: "Find and share tools, knowledge, programs, and opportunities worth using." }} />
    </div>
  );
}
